import {
  Box,
  Button,
  ButtonBase,
  Card,
  CardMedia,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { memo, type ReactNode } from 'react';
import Iconify from 'src/components/iconify';
import type { BenefitDetailsFormValues } from '../benefits-details.hook';
import BenefitsIcon from 'src/assets/icons/benefits-icon';
import BenefitsHeaderLivePreviewBackSvg from './benefits-header-live-preview-back-svg';

type Props = {
  values: BenefitDetailsFormValues;
  themeOptions?: { value: string; label: string }[];
  companyOptions?: { value: string; label: string; image?: string | null }[];
  locationOptions?: { value: string; label: string; image?: string | null; location?: string }[];
};

type StoreOption = {
  value: string;
  label: string;
  image?: string | null;
  location?: string;
};

const SCN_IMAGE =
  'https://cn.backend.roxcode.io/auth/files/getFile/eyJpZCI6IjY3N2ViNGFiMjBiYjllMDViODU1OTMyMyJ9';
const LC_IMAGE =
  'https://cn.backend.roxcode.io/auth/files/getFile/eyJpZCI6IjY3N2VjMDRmNDY4MGRkOGNmYzU1NjQ5NSJ9';

const getImagePreview = (image: BenefitDetailsFormValues['image']) => {
  if (!image) return null;

  if (typeof image === 'string') {
    return image;
  }

  if ('preview' in image && image.preview) {
    return image.preview;
  }

  return null;
};

const getStoreImagePreview = (
  selectedOption: BenefitDetailsFormValues['company'] | BenefitDetailsFormValues['local'],
  options: StoreOption[]
) => {
  if (!selectedOption) {
    return null;
  }

  if (selectedOption.image) {
    return selectedOption.image;
  }

  const matchedOption = options.find(
    (option) => option.value === selectedOption.value || option.label === selectedOption.label
  );

  return matchedOption?.image || null;
};

function BenefitsLivePreview({
  values,
  themeOptions = [],
  companyOptions = [],
  locationOptions = [],
}: Props) {
  const theme = useTheme();
  const isCoupon = values.benefitType === 'coupon';
  const points = String(values.price || 0);
  const selectedThemeLabel = themeOptions.find((option) => option.value === values.theme)?.label;
  const specialTheme = selectedThemeLabel || '';
  const hasSpecialTheme = Boolean(selectedThemeLabel);
  const expirationDays = Number(values.expirationDays) > 0 ? Number(values.expirationDays) : 1;
  const imagePreview = getImagePreview(values.image);
  const selectedLocationOption = locationOptions.find(
    (option) => option.value === values.local?.value || option.label === values.local?.label
  );
  const storeImagePreview =
    values.localType === 'company'
      ? getStoreImagePreview(values.company, companyOptions)
      : getStoreImagePreview(values.local, locationOptions);
  const normalizedLocalContext = (
    selectedLocationOption?.location ||
    values.location ||
    values.local?.label ||
    ''
  )
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
  const isLarCenterLocal = values.localType === 'local' && normalizedLocalContext === 'lar center';
  const resolvedStoreImagePreview = storeImagePreview || (isLarCenterLocal ? LC_IMAGE : SCN_IMAGE);
  const storeLabel =
    values.localType === 'company'
      ? values.company?.label || 'Empresa'
      : values.local?.label || 'Local';
  const localLabel = values.location || 'Local de retirada';

  const isGiftCard = Boolean(values.isGift);
  const endDate = values.endDate ? new Date(values.endDate) : null;
  const formattedEndDate = endDate
    ? endDate.toLocaleDateString('pt-BR', { timeZone: 'UTC' })
    : '--/--/----';
  const activeTabLabel = isCoupon ? 'Cupons' : 'Produtos';
  const headerTitle = activeTabLabel.toUpperCase();
  const previewPoints = new Intl.NumberFormat('pt-BR').format(1000);
  const previewMaxHeight = 900;

  const renderPhoneFrame = (content: ReactNode) => (
    <Box
      sx={{
        width: { xs: '100%', sm: 390 },
        maxWidth: '100%',
        maxHeight: `min(calc(100vh - 118px), ${previewMaxHeight}px)`,
        mx: 'auto',
        p: 1,
        borderRadius: '40px',
        border: '1px solid rgba(15, 23, 42, 0.25)',
        background: 'linear-gradient(160deg, #1f2937 0%, #0f172a 100%)',
        boxShadow:
          '0 28px 52px rgba(2, 6, 23, 0.36), inset 0 1px 0 rgba(255, 255, 255, 0.16), inset 0 -1px 0 rgba(15, 23, 42, 0.4)',
        position: 'relative',
        '@media (max-width: 1600px)': {
          zoom: 0.92,
          maxHeight: `min(calc((100vh - 118px) / 0.92), ${previewMaxHeight}px)`,
        },
        '@media (max-width: 1440px)': {
          zoom: 0.84,
          maxHeight: `min(calc((100vh - 118px) / 0.84), ${previewMaxHeight}px)`,
        },
        '@media (max-width: 1280px)': {
          zoom: 0.76,
          maxHeight: `min(calc((100vh - 118px) / 0.76), ${previewMaxHeight}px)`,
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 132,
          height: 22,
          borderRadius: '0 0 14px 14px',
          backgroundColor: '#020617',
          zIndex: 3,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 6,
            borderRadius: 999,
            backgroundColor: 'rgba(148, 163, 184, 0.6)',
            mt: 0.8,
            mx: 'auto',
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 74,
          right: -2,
          width: 2,
          height: 46,
          borderRadius: '2px 0 0 2px',
          backgroundColor: '#475569',
          opacity: 0.8,
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 122,
          left: -2,
          width: 2,
          height: 34,
          borderRadius: '0 2px 2px 0',
          backgroundColor: '#475569',
          opacity: 0.8,
        }}
      />

      <Box
        sx={{
          borderRadius: '32px',
          overflow: 'hidden',
          backgroundColor: '#EAEEF5',
          height: `min(calc(100vh - 134px), ${previewMaxHeight - 16}px)`,
          maxHeight: `min(calc(100vh - 134px), ${previewMaxHeight - 16}px)`,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          pt: 2.8,
          '@media (max-width: 1600px)': {
            height: `min(calc((100vh - 134px) / 0.92), ${previewMaxHeight - 16}px)`,
            maxHeight: `min(calc((100vh - 134px) / 0.92), ${previewMaxHeight - 16}px)`,
          },
          '@media (max-width: 1440px)': {
            height: `min(calc((100vh - 134px) / 0.84), ${previewMaxHeight - 16}px)`,
            maxHeight: `min(calc((100vh - 134px) / 0.84), ${previewMaxHeight - 16}px)`,
          },
          '@media (max-width: 1280px)': {
            height: `min(calc((100vh - 134px) / 0.76), ${previewMaxHeight - 16}px)`,
            maxHeight: `min(calc((100vh - 134px) / 0.76), ${previewMaxHeight - 16}px)`,
          },
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 80,
            bgcolor: '#EAEEF5',
            borderBottom: '1px solid #d1d5db',
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            spacing={0.5}
            sx={{ position: 'absolute', left: 24 }}
          >
            <BenefitsHeaderLivePreviewBackSvg />
            <Typography
              variant={'inputS700' as any}
              sx={{ color: '#283C75', fontWeight: 700, fontSize: '0.75rem' }}
            >
              Voltar
            </Typography>
          </Stack>

          <Typography
            sx={{
              color: '#283C75',
              fontWeight: 400,
              fontSize: '1.5rem',
              fontFamily: '"Bebas Neue", "BebasNeue", sans-serif',
              letterSpacing: 0.8,
            }}
          >
            {headerTitle}
          </Typography>

          <Box
            sx={{
              position: 'absolute',
              right: 24,
              width: 24,
              height: 24,
              borderRadius: '50%',
              bgcolor: '#9ca3af',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Iconify icon="mdi:user" sx={{ width: 16, height: 16, color: '#f9fafb' }} />
          </Box>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 3,
            height: 56,
            bgcolor: '#283C75',
            color: 'common.white',
          }}
        >
          <Typography
            sx={{ fontWeight: 400, fontSize: '0.875rem', fontFamily: '"Inter", sans-serif' }}
            color="inherit"
          >
            Meus Pontos
          </Typography>

          <Stack direction="row" alignItems="center" spacing={0.8}>
            <Iconify
              icon="lucide:repeat"
              sx={{
                width: 16,
                height: 16,
              }}
            />
            <Typography color="inherit" sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
              {previewPoints}
            </Typography>
          </Stack>
        </Stack>

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              width: 0,
              height: 0,
              display: 'none',
            },
          }}
        >
          {content}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            position: 'relative',
            zIndex: 2,
            px: 1.2,
            py: 2,
            bgcolor: 'common.white',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -16,
              left: 0,
              right: 0,
              height: 16,
              pointerEvents: 'none',
              background:
                'linear-gradient(360deg, rgba(40, 60, 117, 0.07) 0%, rgba(40, 60, 117, 0) 100%)',
            },
          }}
        >
          {[
            { icon: 'lineicons:home-2', label: 'Início' },
            { icon: 'lucide:search', label: 'Buscar' },
            { icon: 'lucide:scan-line', label: 'Escanear' },
            { icon: 'tabler:gift-filled', label: 'Benefícios', active: true },
            { icon: 'basil:layout-outline', label: 'Menu' },
          ].map((item) => (
            <Stack key={item.label} spacing={1} alignItems="center" sx={{ minWidth: 56 }}>
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: item.active ? '#283C75' : '#0f172a',
                }}
              >
                {item.active ? (
                  <BenefitsIcon />
                ) : (
                  <Iconify icon={item.icon} sx={{ width: 24, height: 24 }} color="#283C75" />
                )}
              </Box>
              <Typography
                variant={'caption' as any}
                sx={{
                  fontSize: '0.68rem',
                  lineHeight: 1,
                  color: item.active ? '#283C75' : '#0f172a',
                  fontWeight: item.active ? 700 : 500,
                }}
              >
                {item.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
    </Box>
  );

  if (isCoupon) {
    return renderPhoneFrame(
      <Box sx={{ bgcolor: '#EAEEF5', minHeight: '100%' }}>
        <Box
          sx={{
            height: 150,
            width: '100%',
            backgroundImage: imagePreview
              ? `linear-gradient(180deg, rgba(15, 23, 42, 0.18) 0%, rgba(15, 23, 42, 0.06) 100%), url(${imagePreview})`
              : 'linear-gradient(135deg, #cfd5df 0%, #e9edf4 100%)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />

        <Box sx={{ px: 2, pb: 2.6, mt: -5 }}>
          <Card
            sx={{
              p: 2.5,
              borderRadius: 0.3,
              boxShadow: '0 2px 8px rgba(15, 23, 42, 0.12)',
              position: 'relative',
              overflow: 'visible',
              zIndex: 1,
              backgroundColor: 'white',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '50%',
                left: '-10px',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#EAEEF5',
                zIndex: 2,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                top: '50%',
                right: '-10px',
                transform: 'translateY(-50%)',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                backgroundColor: '#EAEEF5',
                zIndex: 2,
              },
            }}
          >
            <Stack spacing={2}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                {resolvedStoreImagePreview ? (
                  <CardMedia
                    component="img"
                    sx={{ width: 56, height: 56, borderRadius: 0.5 }}
                    image={resolvedStoreImagePreview}
                    alt={`${storeLabel} logo`}
                  />
                ) : (
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 0.5,
                      backgroundColor: '#e5e7eb',
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  />
                )}

                <Stack spacing={0.2} flex={1} minWidth={0}>
                  <Typography
                    variant={'inputM700' as any}
                    sx={{ textTransform: 'uppercase', fontWeight: 700, fontSize: '0.75rem' }}
                  >
                    {storeLabel}
                  </Typography>
                  <Typography
                    sx={{ fontSize: '0.75rem', fontWeight: 400 }}
                    variant={'paragraphP400' as any}
                  >
                    {storeLabel}
                  </Typography>
                </Stack>
              </Stack>

              <Stack spacing={0.8}>
                <Typography
                  variant={'paragraphM700' as any}
                  sx={{ fontWeight: 700, fontSize: '0.875rem' }}
                >
                  {values.description || 'Descrição'}
                </Typography>

                <Typography
                  sx={{ fontSize: '0.75rem', fontWeight: 400 }}
                  variant={'paragraphP400' as any}
                >
                  {values.secondaryDescription || ''}
                </Typography>

                <Typography
                  sx={{ fontSize: '0.75rem', fontWeight: 400 }}
                  variant={'paragraphP400' as any}
                >
                  Regras de utilização:
                </Typography>

                <Typography
                  variant={'paragraphP400' as any}
                  component="div"
                  sx={{
                    wordBreak: 'break-word',
                    '& ul': { m: 0, pl: 4.5 },
                    '& li': { mb: 0.6 },
                    fontSize: '0.75rem',
                    fontWeight: 400,
                  }}
                  dangerouslySetInnerHTML={{
                    __html: values.rules || '<ul><li>Regras</li></ul>',
                  }}
                />
              </Stack>

              <Divider sx={{ borderBottom: `1px dashed ${theme.palette.grey[600]}` }} />

              <Stack direction="row" justifyContent="space-between" pt={0.4}>
                {!!points && points !== '0' ? (
                  <Stack>
                    <Typography
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 400,
                      }}
                      variant={'paragraphP400' as any}
                    >
                      Troque
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Iconify icon="mage:exchange-b" sx={{ width: 16, height: 16 }} />
                      <Typography
                        variant={'inputL700' as any}
                        sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                      >
                        {points} pts
                      </Typography>
                    </Stack>
                  </Stack>
                ) : null}

                <Stack
                  direction={!!points && points !== '0' ? 'column' : 'row'}
                  sx={{
                    width: !!points && points !== '0' ? 'auto' : '100%',
                    justifyContent: !!points && points !== '0' ? undefined : 'space-between',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 400,
                    }}
                    variant={'paragraphP400' as any}
                  >
                    Resgate até
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Iconify icon="formkit:date" sx={{ width: 16, height: 16 }} />
                    <Typography
                      variant={'inputM700' as any}
                      sx={{ fontWeight: 700, fontSize: '0.75rem' }}
                    >
                      {formattedEndDate}
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              <Stack alignItems="center" spacing={2} pt={0.6}>
                <ButtonBase>
                  <Typography
                    variant={'inputL700' as any}
                    sx={{
                      color: (theme.palette as any).blueBrand?.[400] || '#0f3d8c',
                      borderBottom: `2px solid ${(theme.palette as any).blueBrand?.[400] || '#0f3d8c'}`,
                      // cursor: 'pointer',
                      display: 'inline-block',
                      fontWeight: 700,
                      fontSize: '0.875rem',
                    }}
                  >
                    Ver todas as regras
                  </Typography>
                </ButtonBase>

                <Button
                  variant="contained"
                  sx={{
                    py: 1.3,
                    fontSize: '0.875rem',
                    borderRadius: 0.3,
                    ':hover': {
                      bgcolor: (theme.palette as any).blueBrand?.[500] || '#0b2a6c',
                    },
                    bgcolor: (theme.palette as any).blueBrand?.[400] || '#0f3d8c',
                  }}
                  fullWidth
                >
                  Eu quero
                </Button>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>
    );
  }

  return renderPhoneFrame(
    <Card
      sx={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        // backgroundColor: theme.palette.grey[100],
        bgcolor: '#EAEEF5',
        boxShadow: 'none',
        borderRadius: 0,
        p: { xs: 2.6, lg: 3 },
      }}
    >
      <Stack spacing={2}>
        <Stack direction="row" sx={{ display: 'flex' }} alignItems="center" spacing={1.5}>
          {resolvedStoreImagePreview ? (
            <CardMedia
              component="img"
              sx={{ width: 70, height: 70, borderRadius: 0.3 }}
              image={resolvedStoreImagePreview}
              alt={`${storeLabel} logo`}
            />
          ) : (
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: 1,
                backgroundColor: 'common.white',
                border: '1px solid',
                borderColor: 'grey.300',
              }}
            />
          )}

          <Stack spacing={0.2} flex={1}>
            <Typography
              variant={'labelL' as any}
              color="common.neutral"
              sx={{ fontWeight: 700, fontSize: '1rem' }}
            >
              {storeLabel}
            </Typography>
            <Stack direction="row" display="flex" alignItems="center" spacing={0.3}>
              <Iconify icon="lucide:map-pin" sx={{ width: 16, height: 16 }} />
              <Typography
                sx={{ fontSize: '0.875rem' }}
                variant={'paragraphM400' as any}
                color="common.neutral"
                mt={0.2}
                flex={1}
              >
                {localLabel}
              </Typography>
            </Stack>
          </Stack>
        </Stack>

        {(isGiftCard || hasSpecialTheme) && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isGiftCard && hasSpecialTheme ? '1fr 1fr' : '1fr',
              gap: 1.5,
            }}
          >
            {isGiftCard && (
              <Box
                sx={{
                  bgcolor: 'grey.900',
                  py: 1,
                  px: 2,
                  borderRadius: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 0,
                }}
              >
                <Typography
                  variant={'paragraphP400' as any}
                  color="common.white"
                  sx={{
                    textTransform: 'uppercase',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontSize: '0.75rem',
                  }}
                >
                  Vale-Presente
                </Typography>
              </Box>
            )}

            {hasSpecialTheme && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'grey.700',
                  py: 1,
                  px: 1,
                  borderRadius: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 0,
                }}
              >
                <Typography
                  variant={'inputS700' as any}
                  sx={{
                    fontWeight: 700,
                    width: '100%',
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    textTransform: 'uppercase',
                    textAlign: 'center',
                  }}
                >
                  Especial {specialTheme}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {imagePreview ? (
          <Box
            component="img"
            src={imagePreview}
            alt="Imagem de destaque"
            sx={{
              height: 150,
              width: '100%',
              borderRadius: 0.5,
              objectFit: 'cover',
              backgroundColor: 'common.white',
            }}
          />
        ) : (
          <Box
            sx={{
              height: 150,
              borderRadius: 0.5,
              backgroundColor: 'common.white',
              border: '1px dashed',
              borderColor: 'grey.400',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.disabled',
            }}
          >
            Imagem de destaque
          </Box>
        )}

        <Stack spacing={1.6}>
          <Typography
            variant={'labelL' as any}
            color={(theme.palette as any).blueBrand?.[400] || 'primary.main'}
            sx={{ fontWeight: 700, fontSize: '1rem' }}
          >
            {values.name || 'Nome do Produto'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.75rem',
            }}
            variant={'paragraphP400' as any}
          >
            {values.description || 'Descrição'}
          </Typography>
        </Stack>

        <Divider
          sx={{
            my: -1,
          }}
        />

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Typography
            variant={'paragraphP500' as any}
            sx={{ maxWidth: '45%', fontSize: '0.75rem' }}
          >
            Utilize ou retire em até{' '}
            <Box component="span" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
              {expirationDays} dias
            </Box>{' '}
            após o resgate
          </Typography>

          <Stack
            direction="row"
            px={1.4}
            py={0.9}
            bgcolor="#F6BD74"
            alignItems="center"
            spacing={0.8}
            borderRadius={0.3}
          >
            <Iconify icon="mage:exchange-b" sx={{ color: 'common.black', width: 18, height: 18 }} />
            <Typography variant={'labelL' as any} color="common.black" sx={{ fontWeight: 700 }}>
              {points} pts
            </Typography>
          </Stack>
        </Stack>

        <Button
          variant="contained"
          sx={{
            py: 1.3,
            fontSize: '0.875rem',
            borderRadius: 0.3,
            ':hover': {
              bgcolor: (theme.palette as any).blueBrand?.[500] || '#0b2a6c',
            },
            bgcolor: (theme.palette as any).blueBrand?.[400] || '#0f3d8c',
          }}
          fullWidth
        >
          Eu quero
        </Button>

        <Button variant="text" sx={{ alignSelf: 'center' }}>
          <Box
            component="span"
            sx={{
              textDecoration: 'underline',
              color: (theme.palette as any).blueBrand?.[400] || '#0f3d8c',
              fontSize: '0.875rem',
            }}
          >
            Ver todas as regras
          </Box>
        </Button>
      </Stack>
    </Card>
  );
}

export default memo(BenefitsLivePreview);
